/* eslint-disable @nice-cxone/ccf/required-tsdoc */
import { useState, useEffect, useRef } from 'react';
import { Button, Typography, TextField, Box, Snackbar } from '@mui/material';
import { LiveVoiceWSClient } from '@nice-ccf/core-sdk';
import {
  getCopilotConfig,
  setAudioTranscript,
  setIsRequestProcessing,
} from '../ccf-app-space/ccf-copilot/ccf-copilot-chat/+state/ccf-live-copilot.slice';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';
import { processApiResponse } from '../ccf-app-space/ccf-copilot/ccf-copilot-util';
import { BroadcastChannel } from 'broadcast-channel';
import {
  API_BASE_URL,
  callApi,
  PROCESS_UTTERANCE,
} from '../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { CcfTypography } from '@nice-ccf/ui-controls';

const WEBSOCKET_URL = 'wss://zesrxbvpy8.execute-api.us-west-2.amazonaws.com/dev'; // need to remove this during API gateway integration
//const WEBSOCKET_URL = 'ws://localhost:8080';

export const CcfPatron = () => {
  const dispatch = useDispatch();
  const namespace = uuidv5('namespace', namespaceNIL);
  const [contactId, setContactId] = useState<string>('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [toastOpen, setToastOpen] = useState(false);
  const wsClient = useRef<LiveVoiceWSClient | null>(null);
  // const copilotConfigurations = useSelector(getCopilotConfig);
  const copilotConfigurations = JSON.parse(localStorage.getItem('agentAssistAppConfig') as string) || {};
  const languageCode =
    copilotConfigurations?.language?.toLowerCase() ?? 'en-us';
  const [transcript, setTranscript] = useState<string>('');
  const [connectionId, setConnectionId] = useState<string>(''); 

  const requestBody = {
    contactId: contactId,
    busNo: 12345678,
    tenantId: '12345678',
    participantId: '',
    messageBody: '',
    getSalesOffers: false,
    getCompliance: false,
    getKbCombo: false,
    getProcessSteps: false,
    getConversationAssistance: false,
    messageMetaData: {
      completeUtterance: true,
    },
    transcriptionEventId: '',
    threshold_sales: copilotConfigurations?.threshold_sales ?? 0.75,
    threshold_compliance: copilotConfigurations?.threshold_compliance ?? 0.75,
    agentAssistAppConfig: {
      language: languageCode,
      generativeResponseTone:
        copilotConfigurations?.generativeResponseTone ?? 'Formal',
      channels: ['digital', 'voice'],
      transcriptionProfileName: 'google-test',
      enableSentiment: copilotConfigurations?.enableSentiment ?? true,
      sentimentResponseLength:
        copilotConfigurations?.sentimentResponseLength ?? 20,
      realTimeSummary: copilotConfigurations?.realTimeSummary ?? true,
      realTimeSummaryResponseLength:
        copilotConfigurations?.realTimeSummaryResponseLength ?? 25,
      finalSummary: copilotConfigurations?.finalSummary ?? true,
      finalSummaryResponseLength:
        copilotConfigurations?.finalSummaryResponseLength ?? 65,
      behavioralGuidance: true,
      behavioralGuidanceResponseLength: 10,
      behevioralGuidanceNumberOfResponse: 3,
      enableExpertCopilotIntegration: true,
      expertCopilotURL: copilotConfigurations?.expertCopilotURL ?? '',
      expertCopilotClientKey:
        copilotConfigurations?.expertCopilotClientKey ?? '',
      expertCopilotClientSecret:
        copilotConfigurations?.expertCopilotClientSecret ?? '',
      path: copilotConfigurations?.path ?? '',
      botPersona: copilotConfigurations?.botPersona ?? '',
      kBAnswersPath: copilotConfigurations?.kBAnswersPath ?? '',
      salesOffersPath: copilotConfigurations?.salesOffersPath ?? '',
      compliancePath: copilotConfigurations?.compliancePath ?? '',
      nextBestResponse: copilotConfigurations?.nextBestResponse ?? true,
      nextBestResponseLength:
        copilotConfigurations?.nextBestResponseLength ?? 20,
      nextBestNumberOfResponse:
        copilotConfigurations?.nextBestNumberOfResponse ?? 2,
      knowledgeBaseArticles:
        copilotConfigurations?.knowledgeBaseArticles ?? true,
      knowledgeBaseArticlesResponseLength:
        copilotConfigurations?.knowledgeBaseArticlesResponseLength ?? 2,
      knowledgeBaseWebLinks:
        copilotConfigurations?.knowledgeBaseWebLinks ?? true,
      knowledgeBaseMaximumWebLinks:
        copilotConfigurations?.knowledgeBaseMaximumWebLinks ?? 3,
      knowledgeBaseImages: copilotConfigurations?.knowledgeBaseImages ?? true,
      knowledgeBaseMaximumImages:
        copilotConfigurations?.knowledgeBaseMaximumImages ?? 3,
      knowledgeBaseProcessSteps:
        copilotConfigurations?.knowledgeBaseProcessSteps ?? true,
      knowledgeBaseMaximumSteps:
        copilotConfigurations?.knowledgeBaseMaximumSteps ?? 10,
      threshold: copilotConfigurations?.threshold ?? 0.75,
      vendorId: 'agentCopilot',
      agentAssistId: 'Ved_acp_test123',
      scriptParams: '',
      backAndForthLiveConvo:
        copilotConfigurations?.backAndForthLiveConvo ?? false,
      backAndForthLiveConvoLength:
        copilotConfigurations?.backAndForthLiveConvoLength ?? 4,
    },
    messageIsCompletedUtterance: true,
    agentId: 1898,
    mediaType: 'Voice',
    totalAudioChunksInMilliSeconds: 2040,
  };

  // Establish WebSocket connection on submit
  const handleSubmit = () => {
    if (!contactId.trim()) {
      setError('Contact ID is required.');
      return;
    }

    if (!wsClient.current) {
      wsClient.current = new LiveVoiceWSClient();
      wsClient.current.connect(WEBSOCKET_URL);

      wsClient.current.onOpen = () => {
        setConnectionStatus('Connected');
        wsClient.current?.sendMessage(
          JSON.stringify({
            type: 'CONNECT',
            contactId: contactId.trim(),
            participantId: 'patron',
          })
        );
        setIsConnected(true);
        setToastOpen(true);
        setError(null);
      };

      wsClient.current.onClosed = () => {
        setConnectionStatus('Disconnected');
        // setIsConnected(false);
        wsClient.current?.attemptReconnect({
          retryOptions: { maxRetryAttempts: 3, retryInterval: 1000 },
          url: WEBSOCKET_URL,
        });
      };

      wsClient.current.onError = () => {
        setConnectionStatus('Error');
        setError('WebSocket connection error.');
      };

      wsClient.current.onMessage = (message) => {
        if(message.msg === 'Ok' && message?.connectionId && connectionId !== message.connectionId) {
          console.log('WebSocket message:', message);
          setConnectionId(message.connectionId);
        }
      };
    }
  };

  useEffect(() => {
    if (!mediaRecorderRef.current) {
      getAudioAllow(); // Call to initialize MediaRecorder if not already set
    }
  }, []);


  useEffect(() => {
    console.log('inside useEffect');
    const mediaRecorder = mediaRecorderRef.current;
    if ( mediaRecorder) {
      console.log('inside mediaRecorder present');
      const chunks: BlobPart[] = [];
      const dataAvailableListener = (event: BlobEvent) => handleDataAvailable(event, chunks);
      const stopListener = (event: Event) => handleStop(event, chunks);

      mediaRecorder.addEventListener('dataavailable', dataAvailableListener);
      mediaRecorder.addEventListener('stop', stopListener);

      if (isRecording && mediaRecorder.state !== 'recording') {
        console.log('Starting mediaRecorder');
        mediaRecorder.start(2000); // Start the media recorder
      } else if (!isRecording && mediaRecorder.state === 'recording') {
        console.log('Stopping mediaRecorder');
        mediaRecorder.stop(); // Stop recording
      }

      return () => {
        mediaRecorder.removeEventListener('dataavailable', dataAvailableListener);
        mediaRecorder.removeEventListener('stop', stopListener);
      };
    }
    return;
  }, [isRecording]);

  // Toggle microphone
  const toggleRecording = () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
    } else {
      setTranscript('');
    }
    setIsRecording(!isRecording);
  };


  const getAudioAllow = async () => {
    try {
      const mediaStream = await window.navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mediaRecorder = new MediaRecorder(mediaStream);
      mediaRecorderRef.current = mediaRecorder; // Store the MediaRecorder instance in the ref
      console.log('MediaRecorder initialized');
    } catch (e) {
      console.error(e);
      setError('Error accessing microphone: ' + e);
    }
  };

  // const handleSpeak = async (mediaRecorder: MediaRecorder) => {
  //   const chunks: BlobPart[] = [];

  //   mediaRecorder.addEventListener('dataavailable', (event) => {
  //     if (event.data.size > 0) {
  //       chunks.push(event.data);
  //     }
  //   });

  //   mediaRecorder.start(1000);

  //   setTimeout(() => {
  //     mediaRecorder.stop();
  //   }, 5000);

  //   mediaRecorder.addEventListener('stop', async (event) => {
  //     const blobData = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
  //     const reader = new FileReader();
  //     reader.onloadend = async () => {
  //       const base64Data = (reader?.result as string)?.split(',')[1];
  //       try {
  //         if (wsClient.current) {
  //           wsClient.current.sendMessage(
  //             JSON.stringify({
  //               audioData: base64Data,
  //               language: 'en-us',
  //               participantId: 'patron',
  //             })
  //           );
  //         } else {
  //           console.error('WebSocket client is not initialized');
  //         }
  //       } catch (error) {
  //         console.log('Error sending audio chunk: ', error);
  //       }
  //     };
  //     reader.readAsDataURL(blobData);
  //     setIsRecording(false);
  //   });
  // };

  // Handle incoming WebSocket messages
  
  
  const handleSpeak = async (mediaRecorder: MediaRecorder) => {
    // setIsRecording(true);
  
    mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        const chunk = event.data;
        const reader = new FileReader();
  
        reader.onloadend = () => {
          const base64Data = (reader?.result as string)?.split(',')[1];
          try {
            if (wsClient.current) {
              wsClient.current.sendMessage(
                JSON.stringify({
                  audioData: base64Data,
                  language: 'en-us',
                  participantId: 'patron',
                })
              );
            } else {
              console.error('WebSocket client is not initialized');
            }
          } catch (error) {
            console.log('Error sending audio chunk: ', error);
          }
        };
        reader.readAsDataURL(chunk);
      }
    });
  
    // Start recording and emit data every 1000ms (or adjust as needed)
    mediaRecorder.start(1000);
  
    // Automatically stop the recording after 5 seconds
    // setTimeout(() => {
    //   mediaRecorder.stop();
    //   setIsRecording(false);
    // }, 5000);
  };

    /**
     * Handles the `dataavailable` event by pushing the event's data into the provided chunks array if the data size is greater than 0.
     *
     * @param event - The `BlobEvent` containing the data.
     * @param chunks - The array of `BlobPart` where the event's data will be pushed if available.
     */
    const handleDataAvailable = (event: BlobEvent, chunks: BlobPart[]) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
  
  
    /**
     * Handles the stop event for audio recording, processes the audio chunks, and sends the audio data
     * to a WebSocket server.
     *
     * @param event - The event triggered when the recording stops.
     * @param chunks - An array of BlobPart representing the recorded audio chunks.
     *
     * @returns A promise that resolves when the audio data has been processed and sent.
     *
     * @remarks
     * This function creates a Blob from the audio chunks, converts it to a base64 string, and sends it
     * to a WebSocket server along with additional metadata such as language, participant ID, and contact ID.
     * If the WebSocket client is not initialized, an error message is logged to the console.
     *
     * @throws Will log an error message to the console if there is an issue sending the audio chunk.
     */
    const handleStop = async (event: Event, chunks: BlobPart[]) => {
      const blobData = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = (reader?.result as string)?.split(',')[1];
        try {
          if (wsClient.current) {
            wsClient.current.sendMessage(
              JSON.stringify({
                audioPacket: base64Data,
                // language: 'en-us',
                participantId: 'patron',
                contactId,
                connectionId
              })
            );
          } else {
            console.error('WebSocket client is not initialized');
          }
        } catch (error) {
          console.log('Error sending audio chunk: ', error);
        }
      };
      reader.readAsDataURL(blobData);
    }
  
  const handleWebSocketMessage = (event: MessageEvent) => {
    try {
      if (event.data) {
        const message = event.data;
        if (message && Array.isArray(message)) {
          const audioTranscriptText = message.join(' ');
          console.log('Joined transcription text:', audioTranscriptText);

          if (audioTranscriptText) {
            setTranscript(audioTranscriptText);
            dispatch(setAudioTranscript(audioTranscriptText));
            setTimeout(() => {
              dispatch(setAudioTranscript(''));
            }, 5000);

            const customerRequestBody = {
              ...requestBody,
              messageBody: audioTranscriptText,
              participantId: 'patron',
              transcriptionEventId: uuidv5(audioTranscriptText, namespace),
            };

            // Send transcription result to process-utterance API
            sendToProcessUtteranceAPI(customerRequestBody, audioTranscriptText);
          }
        } else {
          console.warn('Received invalid WebSocket message');
        }
      } else {
        console.warn('Received empty WebSocket message');
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  const sendToProcessUtteranceAPI = (
    customerRequestBody: any,
    audioTranscriptText: string
  ) => {
    if (audioTranscriptText !== '') {
      return new Promise<void>((resolve, reject) => {
        const apiCallPromises = [];
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            customerRequestBody,
            dispatch
          )
            .then((response: any) => {
              // processApiResponse(response.data, dispatch, 'Digital', false);
              resolve();
              const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                'patron_channel'
              );
              acp_channel
                .postMessage({
                  type: 'process-utterance',
                  data: response.data ?? response,
                })
                .then(() => acp_channel.close())
                .catch((error) => {
                  console.error(
                    'Error while posting message to broadcast channel:',
                    error
                  );
                  reject(error);
                });
            })
            .catch((error: any) => {
              console.error('Error:', error);
            })
        );
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            { ...customerRequestBody, getKbCombo: true },
            dispatch
          )
            .then((response: any) => {
              callApi(
                API_BASE_URL + PROCESS_UTTERANCE,
                'POST',
                { ...customerRequestBody, getProcessSteps: true },
                dispatch
              )
                .then((response: any) => {
                  // processApiResponse(response.data, dispatch, 'Digital', true);
                  resolve();
                  const acp_channel: BroadcastChannel<any> =
                    new BroadcastChannel('patron_channel');
                  acp_channel
                    .postMessage({
                      type: 'process-utterance',
                      data: response.data ?? response,
                    })
                    .then(() => acp_channel.close())
                    .catch((error) => {
                      console.error(
                        'Error while posting message to broadcast channel:',
                        error
                      );
                      reject(error);
                    });
                })
                .catch((error: any) => {
                  console.error('Error:', error);
                });
              // processApiResponse(response.data, dispatch);
              resolve();
              const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                'patron_channel'
              );
              acp_channel
                .postMessage({
                  type: 'process-utterance',
                  data: response.data ?? response,
                })
                .then(() => acp_channel.close())
                .catch((error) => {
                  console.error(
                    'Error while posting message to broadcast channel:',
                    error
                  );
                  reject(error);
                });
            })
            .catch((error: any) => {
              console.error('Error:', error);
            })
        );
        // salesOffers
        // salesOffers API call
        if (copilotConfigurations?.salesOffers ?? true) {
          apiCallPromises.push(
            callApi(
              API_BASE_URL + PROCESS_UTTERANCE,
              'POST',
              { ...customerRequestBody, getSalesOffers: true },
              dispatch
            )
              .then((response: any) => {
                //   processApiResponse(response.data, dispatch);
                resolve();
                const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                  'patron_channel'
                );
                acp_channel
                  .postMessage({
                    type: 'process-utterance',
                    data: response.data ?? response,
                  })
                  .then(() => acp_channel.close())
                  .catch((error) => {
                    console.error(
                      'Error while posting message to broadcast channel:',
                      error
                    );
                    reject(error);
                  });
              })
              .catch((error: any) => {
                console.error('Error:', error);
              })
          );
        }
        // compliance
        // compliance API call
        if (copilotConfigurations?.compliance ?? true) {
          apiCallPromises.push(
            callApi(
              API_BASE_URL + PROCESS_UTTERANCE,
              'POST',
              { ...customerRequestBody, getCompliance: true },
              dispatch
            )
              .then((response: any) => {
                //   processApiResponse(response.data, dispatch);
                resolve();
                const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                  'patron_channel'
                );
                acp_channel
                  .postMessage({
                    type: 'process-utterance',
                    data: response.data ?? response,
                  })
                  .then(() => acp_channel.close())
                  .catch((error) => {
                    console.error(
                      'Error while posting message to broadcast channel:',
                      error
                    );
                    reject(error);
                  });
              })
              .catch((error: any) => {
                console.error('Error:', error);
              })
          );
        }
        // conversation Assistance
        // conversation Assistance API call
        apiCallPromises.push(
          callApi(
            API_BASE_URL + PROCESS_UTTERANCE,
            'POST',
            { ...customerRequestBody, getConversationAssistance: true },
            dispatch
          )
            .then((response: any) => {
              // processApiResponse(response.data, dispatch);
              resolve();
              const acp_channel: BroadcastChannel<any> = new BroadcastChannel(
                'patron_channel'
              );
              acp_channel
                .postMessage({
                  type: 'process-utterance',
                  data: response.data ?? response,
                })
                .then(() => acp_channel.close())
                .catch((error) => {
                  console.error(
                    'Error while posting message to broadcast channel:',
                    error
                  );
                  reject(error);
                });
            })
            .catch((error: any) => {
              console.error('Error:', error);
            })
        );
        // Process all API call promises
        Promise.allSettled(apiCallPromises).then(() => {
          dispatch(setIsRequestProcessing(false));
        });

        /* if (intents.length > 0) {
        handleIntent(audioTranscriptText);
      }
      if (customReplies.length > 0) {
        handleBehavioralGuidance(audioTranscriptText);
      }
      if (customerSentiment.length > 0) {
        handleCustomerSentiment(audioTranscriptText);
      } */
      });
    }
    return null;
  };


  useEffect(() => {
    // Cleanup function to run when the component unmounts
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      wsClient.current?.onClosed(); // Close the WebSocket connection
    };
  }, []);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      width="100vw"
      bgcolor="#f5f5f5"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        p={3}
        bgcolor="white"
        boxShadow={3}
        borderRadius={4}
        width="100%"
        maxWidth="400px"
      >
        {!isConnected && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <TextField
              label="Enter Contact ID"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              error={!!error}
              helperText={error}
              fullWidth
              margin="normal"
            />
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              fullWidth
            >
              Submit
            </Button>
          </Box>
        ) /* : (
          <Typography>Status: {connectionStatus}</Typography>
        ) */}

        {isConnected && (
          <Box mt={3}>
            <Button
              variant="contained"
              color={isRecording ? 'secondary' : 'primary'}
              onClick={toggleRecording}
            >
              {!isRecording ? 'Mic On' : 'Mic Off'}
            </Button>
            {error && <Typography color="error">{error}</Typography>}
            <CcfTypography>{transcript}</CcfTypography>
          </Box>
        )}
        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          message="Connected to WebSocket server"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          ContentProps={{
            style: {
              backgroundColor: 'green',
            },
          }}
        />
      </Box>
    </Box>
  );
};
export default CcfPatron;
